import type { CarCardData } from '@/components/cars/CarCard';

export interface ModelGroup {
  modelId: string;
  brand: CarCardData['brand'];
  model: CarCardData['model'];
  variants: CarCardData[];
  minPrice: number | null;
  maxPrice: number | null;
  minRange: number | null;
  maxRange: number | null;
  variantCount: number;
  powertrains: string[];
  hasUpcoming: boolean;
  hasDiscontinued: boolean;
  /** Cheapest variant — used for the canonical link */
  cheapestVariant: CarCardData;
}

export function groupCarsByModel(cars: CarCardData[]): ModelGroup[] {
  const map = new Map<string, CarCardData[]>();
  for (const car of cars) {
    const key = car.model.id;
    const arr = map.get(key);
    if (arr) arr.push(car);
    else map.set(key, [car]);
  }

  const groups: ModelGroup[] = [];
  for (const [modelId, variants] of map) {
    const prices = variants
      .map(v => v.lowestPrice?.price_dkk ?? v.variant.expected_price_dkk ?? null)
      .filter((p): p is number => typeof p === 'number' && p > 0);
    const ranges = variants
      .map(v => v.specs?.range_wltp_km ?? null)
      .filter((r): r is number => typeof r === 'number' && r > 0);
    const powertrains = Array.from(
      new Set(variants.map(v => v.variant.powertrain_type).filter(Boolean) as string[]),
    );
    // Billigste øverst — varianter uden pris til sidst
    const sortedVariants = [...variants].sort((a, b) => {
      const pa = a.lowestPrice?.price_dkk ?? a.variant.expected_price_dkk ?? Infinity;
      const pb = b.lowestPrice?.price_dkk ?? b.variant.expected_price_dkk ?? Infinity;
      return pa - pb;
    });
    const cheapest = sortedVariants[0];


    groups.push({
      modelId,
      brand: variants[0].brand,
      model: variants[0].model,
      variants: sortedVariants,
      minPrice: prices.length ? Math.min(...prices) : null,
      maxPrice: prices.length ? Math.max(...prices) : null,
      minRange: ranges.length ? Math.min(...ranges) : null,
      maxRange: ranges.length ? Math.max(...ranges) : null,
      variantCount: variants.length,
      powertrains,
      hasUpcoming: variants.some(v => v.variant.availability_status === 'upcoming'),
      hasDiscontinued: variants.some(v => v.variant.availability_status === 'discontinued'),
      cheapestVariant: cheapest,
    });
  }
  return groups;
}
